<template>
  <div class="app-install-modal">
    <div class="app-install-modal__content">
      <h2 class="modal-title">Обратите внимание</h2>
      
      <div class="modal-text">
        <p>После установки приложения не забудьте вернуться на этот экран и нажать «Далее»</p>
        <p class="modal-highlight">Это нужно, чтобы настроить VPN — без этого шага он не будет работать</p>
      </div>

      <!-- Выбор приложения -->
      <div v-if="availableApps.length > 1" class="app-selection">
        <h3 class="app-selection__title">Выберите приложение для установки:</h3>
        <div class="app-selection__list">
          <div 
            v-for="(app, index) in availableApps" 
            :key="index"
            class="app-selection__item"
            :class="{ 'app-selection__item--selected': selectedAppIndex === index }"
            @click="selectedAppIndex = index"
          >
            <!-- <div class="app-selection__radio">
              <input 
                type="radio" 
                :id="`app-${index}`"
                :value="index"
                v-model="selectedAppIndex"
                class="app-selection__radio-input"
              />
              <label :for="`app-${index}`" class="app-selection__radio-label"></label>
            </div> -->
            <div class="app-selection__info">
              <span class="app-selection__name">{{ app.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="app-install-modal__footer">
      <div class="modal-actions">
        <button 
          class="install-button"
          @click="handleInstallClick"
        >
          Понятно, установить {{ selectedApp?.name || 'приложение' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { GetConnectItem } from '@/entities/Subscription/model/types'
import { SentryService } from '@/shared/lib/sentry/sentryService'

interface Props {
  availableApps: GetConnectItem[]
  defaultAppIndex?: number
  onAppSelected?: (app: GetConnectItem) => void
}

const props = withDefaults(defineProps<Props>(), {
  defaultAppIndex: 0
})

const emit = defineEmits<{
  close: []
  appSelected: [app: GetConnectItem]
}>()

// Индекс выбранного приложения
const selectedAppIndex = ref(props.defaultAppIndex)

// Вычисляемое свойство для выбранного приложения
const selectedApp = computed(() => {
  return props.availableApps[selectedAppIndex.value] || props.availableApps[0]
})

const handleInstallClick = () => {
  // Отправляем отчет в Sentry
  SentryService.trackButtonClick('Установить приложение', {
    app_name: selectedApp.value?.name || 'unknown',
    available_apps_count: props.availableApps.length,
    modal: 'app_install'
  })
  
  // Открываем ссылку на выбранное приложение
  if (selectedApp.value) {
    window.open(selectedApp.value.download_link, '_blank')
    // Вызываем callback если передан
    if (props.onAppSelected) {
      props.onAppSelected(selectedApp.value)
    }
    // Эмитим событие для совместимости
    emit('appSelected', selectedApp.value)
  }
  // Закрываем модалку
  emit('close')
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/mixins.scss';

.app-install-modal {
  display: flex;
  flex-direction: column;
  text-align: center;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 0;
  }

  &__footer {
    margin-top: 24px;
    padding-top: 16px;
    padding-bottom: 16px;
  }
}

.modal-title {
    @include h1;
    color: $neutral-100;
    margin: 0 0 24px 0;
}

.modal-text {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  
  p {
    @include p2-regular;
    color: $neutral-100;
    margin: 0;
  }
}

.modal-highlight {
  color: $primary-100 !important;
}

.app-selection {
  margin-top: 24px;
  
  &__title {
    @include p2-medium;
    color: $neutral-100;
    margin: 0 0 16px 0;
  }
  
  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border: 1px solid $secondary-200;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: $primary-100;
      background: rgba($primary-100, 0.05);
    }
    
    &--selected {
      border-color: $primary-100;
      background: rgba($primary-100, 0.1);
    }
  }
  
  &__radio {
    position: relative;
    flex-shrink: 0;
  }
  
  &__radio-input {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
  }
  
  &__radio-label {
    display: block;
    width: 20px;
    height: 20px;
    border: 2px solid $secondary-200;
    border-radius: 50%;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: $primary-100;
      transform: translate(-50%, -50%) scale(0);
      transition: transform 0.2s ease;
    }
  }
  
  &__radio-input:checked + &__radio-label {
    border-color: $primary-100;
    
    &::after {
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  &__info {
    flex: 1;
  }
  
  &__name {
    @include p2-medium;
    color: $neutral-100;
  }
}

.modal-actions {
  width: 100%;
}

.install-button {
    @include p2-regular;
    width: 100%;
    padding: 16px 24px;
    background: $secondary-100;
    border: none;
    border-radius: 16px;
    color: $neutral-100;
    cursor: pointer;

    &:hover {
        background: $secondary-100-active;
    }
}
</style>